import React, { useState } from 'react';
import { X, Download, CheckCircle } from 'lucide-react';
import { Document, Page, Text, View, StyleSheet, PDFDownloadLink, pdf } from '@react-pdf/renderer';

// PDF Styles
const pdfStyles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: 'Helvetica',
    fontSize: 11,
  },
  header: {
    marginBottom: 30,
    borderBottom: '2 solid #3B0069',
    paddingBottom: 15,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#3B0069',
    marginBottom: 5,
  },
  subtitle: {
    fontSize: 12,
    color: '#666',
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#232323',
    marginBottom: 10,
    backgroundColor: '#F5F5F5',
    padding: 8,
  },
  row: {
    flexDirection: 'row',
    marginBottom: 8,
    paddingHorizontal: 8,
  },
  label: {
    width: '40%',
    fontSize: 11,
    color: '#737373',
  },
  value: {
    width: '60%',
    fontSize: 11,
    fontWeight: 'bold',
    color: '#232323',
  },
  amountBox: {
    backgroundColor: '#ECE6F0',
    padding: 15,
    marginVertical: 15,
    borderRadius: 4,
    alignItems: 'center',
  },
  amountLabel: {
    fontSize: 12,
    color: '#3B0069',
    marginBottom: 5,
  },
  amountValue: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#3B0069',
  },
  footer: {
    position: 'absolute',
    bottom: 40,
    left: 40,
    right: 40,
    borderTop: '1 solid #CCCCCC',
    paddingTop: 10,
    fontSize: 9,
    color: '#999',
    textAlign: 'center',
  },
  statusBadge: {
    backgroundColor: '#E8F5E9',
    color: '#2E7D32',
    padding: 6,
    borderRadius: 4,
    fontSize: 10,
    textAlign: 'center',
    marginTop: 5,
  },
});

// PDF Document Component
const PaymentReceipt = ({ payment, contractor }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        {/* Header */}
        <View style={pdfStyles.header}>
          <Text style={pdfStyles.title}>Payment Receipt</Text>
          <Text style={pdfStyles.subtitle}>
            Receipt ID: #ContractorPay_WO_{String(payment.workorder_id).padStart(3, '0')}
          </Text>
        </View>

        {/* Payment Amount */}
        <View style={pdfStyles.amountBox}>
          <Text style={pdfStyles.amountLabel}>Amount Paid</Text>
          <Text style={pdfStyles.amountValue}>₹{payment.amount.toLocaleString('en-IN')}</Text>
          <View style={pdfStyles.statusBadge}>
            <Text>✓ Payment Completed</Text>
          </View>
        </View>

        {/* Contractor Information */}
        <View style={pdfStyles.section}>
          <Text style={pdfStyles.sectionTitle}>Contractor Information</Text>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>Name:</Text>
            <Text style={pdfStyles.value}>{contractor.name}</Text>
          </View>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>Contact:</Text>
            <Text style={pdfStyles.value}>{contractor.contact || 'N/A'}</Text>
          </View>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>Address:</Text>
            <Text style={pdfStyles.value}>{contractor.address || 'N/A'}</Text>
          </View>
        </View>

        {/* Payment Details */}
        <View style={pdfStyles.section}>
          <Text style={pdfStyles.sectionTitle}>Payment Details</Text>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>Work Order ID:</Text>
            <Text style={pdfStyles.value}>WO_{String(payment.workorder_id).padStart(3, '0')}</Text>
          </View>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>Site/Project:</Text>
            <Text style={pdfStyles.value}>{payment.site}</Text>
          </View>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>Payment Method:</Text>
            <Text style={pdfStyles.value}>{payment.method}</Text>
          </View>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>Payment Type:</Text>
            <Text style={pdfStyles.value}>{payment.payment_type}</Text>
          </View>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>Payment Date:</Text>
            <Text style={pdfStyles.value}>{formatDate(payment.payment_date)}</Text>
          </View>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>Payment Time:</Text>
            <Text style={pdfStyles.value}>{formatTime(payment.updated_on)}</Text>
          </View>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>Paid By:</Text>
            <Text style={pdfStyles.value}>{payment.paidBy}</Text>
          </View>
        </View>

        {/* Footer */}
        <View style={pdfStyles.footer}>
          <Text>This is a computer-generated receipt and does not require a signature.</Text>
          <Text>Generated on {new Date().toLocaleString('en-IN')}</Text>
        </View>
      </Page>
    </Document>
  );
};

// Modal Component
export const PaymentDetailModal = ({ payment, contractor, onClose }) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const convertToStringIndex = (num) => {
    return String(num).padStart(3, '0');
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-[#3B0069] to-[#5E2D91] text-white p-6 rounded-t-2xl">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold mb-2">Payment Receipt</h2>
              <p className="text-sm opacity-90">
                #ContractorPay_WO_{convertToStringIndex(payment.workorder_id)}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Amount Section */}
          <div className="bg-gradient-to-br from-[#ECE6F0] to-[#F5F5F5] rounded-xl p-6 text-center border-2 border-[#3B0069]">
            <div className="flex items-center justify-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <p className="text-sm font-medium text-[#737373]">Amount Paid</p>
            </div>
            <p className="text-4xl font-bold text-[#3B0069]">
              ₹{payment.amount.toLocaleString('en-IN')}
            </p>
            <div className="mt-3">
              <span className="bg-green-100 text-green-700 text-xs font-medium px-3 py-1 rounded-full">
                ✓ Payment Completed
              </span>
            </div>
          </div>

          {/* Contractor Information */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-[#232323] border-b-2 border-[#E3E3E3] pb-2">
              Contractor Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-[#737373]">Name</p>
                <p className="text-base font-medium text-[#232323]">{contractor.name}</p>
              </div>
              <div>
                <p className="text-sm text-[#737373]">Contact</p>
                <p className="text-base font-medium text-[#232323]">{contractor.contact || 'N/A'}</p>
              </div>
              <div className="col-span-2">
                <p className="text-sm text-[#737373]">Address</p>
                <p className="text-base font-medium text-[#232323]">{contractor.address || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Payment Details */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-[#232323] border-b-2 border-[#E3E3E3] pb-2">
              Payment Details
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-[#737373]">Work Order ID</p>
                <p className="text-base font-medium text-[#232323]">
                  WO_{convertToStringIndex(payment.workorder_id)}
                </p>
              </div>
              <div>
                <p className="text-sm text-[#737373]">Site/Project</p>
                <p className="text-base font-medium text-[#3B0069]">{payment.site}</p>
              </div>
              <div>
                <p className="text-sm text-[#737373]">Payment Method</p>
                <span className="inline-block bg-[#ECE6F0] text-[#3B0069] text-sm font-medium px-3 py-1 rounded-full">
                  {payment.method}
                </span>
              </div>
              <div>
                <p className="text-sm text-[#737373]">Payment Type</p>
                <span className="inline-block bg-[#F5F5F5] text-[#737373] text-sm font-medium px-3 py-1 rounded-full">
                  {payment.payment_type}
                </span>
              </div>
              <div>
                <p className="text-sm text-[#737373]">Payment Date & Time</p>
                <p className="text-base font-medium text-[#232323]">
                  {formatDateTime(payment.payment_date)}
                </p>
              </div>
              <div>
                <p className="text-sm text-[#737373]">Paid By</p>
                <p className="text-base font-medium text-[#232323]">{payment.paidBy}</p>
              </div>
            </div>
          </div>

          {/* Download Button */}
          <div className="pt-4">
            <PDFDownloadLink
              document={<PaymentReceipt payment={payment} contractor={contractor} />}
              fileName={`Payment_Receipt_WO_${convertToStringIndex(payment.workorder_id)}.pdf`}
              className="w-full"
            >
              {({ blob, url, loading, error }) => (
                <button
                  disabled={loading}
                  className="w-full bg-[#3B0069] hover:bg-[#5E2D91] text-white font-semibold py-3 px-6 rounded-lg flex items-center justify-center gap-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download className="w-5 h-5" />
                  {loading ? 'Generating PDF...' : 'Download Receipt as PDF'}
                </button>
              )}
            </PDFDownloadLink>
          </div>
        </div>
      </div>
    </div>
  );
};
